import KhungNoiDungUpload from '@/models/KhungNoiDungUpload.model';
import { BaseService, type PrefixType } from '../base.service';
import { _API_URL } from '@/actions/constants';
import axiosInstance from '../axiosInstance';

/**
 * SSE event data từ UploadFolderStream
 */
export interface UploadStreamEvent {
  event: 'started' | 'file' | 'finished' | 'error';
  total?: number;
  overallPercent?: number;
  // file event
  status?: 'Processing' | 'Completed' | 'Failed';
  progressPercent?: number;
  relativePath?: string;
  fileName?: string;
  storedPath?: string;
  error?: string;
  message?: string;
  // finished event
  result?: {
    total: number;
    success: number;
    failed: number;
    items: Array<{
      relativePath: string;
      storedPath: string | null;
      isSuccess: boolean;
      error: string | null;
    }>;
  };
}

/**
 * Service upload khung nội dung qua GateWay
 */
class KhungNoiDungUploadService extends BaseService<KhungNoiDungUpload, string> {
  constructor(prefix: PrefixType = 'auth', suffix: string = '') {
    super(KhungNoiDungUpload, prefix, suffix);
  }

  /**
   * Upload thư mục qua SSE stream (tiến độ real-time)
   * Dùng fetch (không dùng EventSource vì cần POST + body)
   */
  async UploadFolderStream(
    key: string,
    files: File[],
    relativePaths: string[],
    positions: number[] | null,
    onProgress?: (evt: UploadStreamEvent) => void,
  ): Promise<UploadStreamEvent | null> {
    const formData = new FormData();
    formData.append('key', key);
    files.forEach((file) => formData.append('files', file));
    relativePaths.forEach((path) => formData.append('relativePaths', path));
    if (positions && positions.length > 0) {
      positions.forEach((pos) => formData.append('positions', String(pos)));
    }

    // Lấy headers từ axiosInstance (gồm X-GDT-Dev-Token, Authorization nếu có)
    const defaultHeaders = axiosInstance.defaults.headers.common || {};
    const headers: Record<string, string> = {};
    for (const [headerKey, headerVal] of Object.entries(defaultHeaders)) {
      if (typeof headerVal === 'string') {
        headers[headerKey] = headerVal;
      }
    }

    const host = `${_API_URL}${this.endpoint}/UploadFolderStream`;

    const res = await fetch(host, {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: formData,
    });

    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finishedResult: UploadStreamEvent | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? '';

      for (const block of blocks) {
        const dataLine = block
          .split('\n')
          .find((l: string) => l.startsWith('data: '));
        if (!dataLine) continue;
        try {
          const evt: UploadStreamEvent = JSON.parse(dataLine.slice(6));
          if (onProgress) onProgress(evt);
          if (evt.event === 'finished') finishedResult = evt;
          if (evt.event === 'error')
            throw new Error(evt.message || 'Stream error');
        } catch (e: any) {
          if (e.message === 'Stream error') throw e;
        }
      }
    }
    return finishedResult;
  }
}

export default new KhungNoiDungUploadService();
